import { useAuth0, useModal } from "@/Contexts";
import { useTerminalsState } from "@/States/Terminals";
import { isCarrotAdmin } from "@/Utils/authUtils";
import { setMultiTenantHeaders } from "@/Utils/headerUtils";
import { trpc } from "@/Utils/trpc";
import { useNavigate } from "@/Utils/useNavigate";
import { addAndTriggerDownloadElement, getExportUrl } from "@/api/export";
import { timezoneStore, useConfig } from "@/api/hooks/useConfig";
import useCustomers from "@/api/hooks/useCustomers";
import { TypeEnum } from "@/api/hooks/useCustomers/useCustomers";
import { useMinimalCustomers } from "@/api/hooks/useCustomers/useMinimalCustomers";
import { useSelectedSite } from "@/api/hooks/useSelectedSite";
import { AddButton } from "@/components/AddButton";
import { Page } from "@/components/Page";
import { Header } from "@/components/TableWithSelect";
import { OPTION_ALL } from "@/components/pageCards/filterSort/constants";
import {
  FilterSort,
  FilterSortMenuType,
} from "@/components/pageCards/filterSort/types";
import MultiListTable from "@/components/pageCards/multiListTable";
import { MultiListSectionConfig } from "@/components/pageCards/multiListTable/multiListSection";
import { useMultiListInitialState } from "@/components/pageCards/multiListTable/multiListState";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { CustomerDrawer } from "@/pages/customer/components/CustomerDrawer";
import { useTrans } from "@/translations";

import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Cell } from "@tanstack/react-table";
import moment from "moment";
import { throttle } from "throttle-debounce";

import { NoAccessParentsForCustomersModal } from "./components/NoAccessParentsForCustomersModal";
import { CreateCustomerModal } from "./components/mwm/CreateCustomerModal";
import { CreateTenantModal } from "./components/realEstate/CreateTenantModal";

export const ManageCustomers: FC<{}> = () => {
  const {
    data: tenants,
    isLoadingCustomers,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    sortingState,
    setSortingState,
    setTenantsFilterState,
    tenantsFilterState,
    selectedIds,
    setSelectedIds,
    allSelected,
    setAllSelected,
    nameFilter,
    setNameFilter,
  } = useCustomers();
  const { id: customerIdFromUrl } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTrans();
  const { getTokenSilently } = useAuth0()!;
  const { selectedSite } = useSelectedSite();
  const { timezone } = timezoneStore();

  const { config, isRealEstate, isMWM } = useConfig();
  const { terminalConfig } = useConfigService();

  const { currentTerminal } = useTerminalsState();

  const { data: accessParents } = trpc.accessParents.getAll.useQuery({
    terminalId: isMWM ? undefined : currentTerminal?.id,
  });
  const { usedTenantCategories } = useMinimalCustomers();
  const [isQRExportLoading, setIsQRExportLoading] = useState<boolean>(false);

  const selectedCustomers = useMemo(
    () =>
      tenants?.filter((customer) => selectedIds.includes(customer.id)) ?? [],
    [tenants, selectedIds],
  );

  const typeRender = useCallback(
    ({ cell }: { cell: Cell<any, unknown> }) => {
      return t(
        `entities:customerType${(cell.getValue() as string).toUpperCase()}`,
      );
    },
    [t],
  );

  const tableHeaders: Header[] = [
    { key: "name", title: "entities:customerReference", sortable: true },
    config.useExternalCRM && {
      key: "crmId",
      title: "drawerLabels:crmId",
      sortable: false,
    },
    isRealEstate && {
      key: "category",
      title: "statisticsLabels:customerCategory",
      sortable: false,
    },
    isMWM && {
      key: "type",
      title: "statisticsLabels:customerType",
      renderFunction: typeRender,
      sortable: false,
    },
    config.useExternalCRM && {
      key: "address.street",
      title: "propertyLabels:streetAddress",
      sortable: false,
    },
  ].filter(Boolean) as Header[];

  const { showModal } = useModal();

  const accessParentsLength = useMemo(
    () => accessParents?.length,
    [accessParents],
  );

  const createCustomer = useCallback(() => {
    if (accessParentsLength === 0) {
      showModal(<NoAccessParentsForCustomersModal />);
      return;
    }
    if (isRealEstate) {
      showModal(<CreateTenantModal />);
    } else {
      return showModal(<CreateCustomerModal />);
    }
  }, [showModal, accessParentsLength, isRealEstate]);

  useEffect(() => {
    if (
      hasNextPage &&
      customerIdFromUrl &&
      !tenants.find((customer) => customer.id === customerIdFromUrl)
    ) {
      fetchNextPage();
    }
  }, [hasNextPage, customerIdFromUrl, tenants, fetchNextPage]);

  // Handle URL changes
  useEffect(() => {
    if (
      selectedIds.length === 1 &&
      customerIdFromUrl &&
      selectedIds[0] !== customerIdFromUrl &&
      isFetchingNextPage
    ) {
      setSelectedIds([]); // The drawer will read from the URL while data is paginating
    }
  }, [setSelectedIds, selectedIds, customerIdFromUrl, isFetchingNextPage]);

  const customerTypeFilter: FilterSort = useMemo(
    () => ({
      type: "filter",
      menuType: FilterSortMenuType.Select,
      options: [
        {
          option: "filterLabels:allCustomerTypes",
          value: "all",
          translate: true,
        },
        ...Object.values(TypeEnum)
          .filter((key) =>
            !config?.useExternalCRM
              ? !key.toLowerCase().includes("parent")
              : true,
          )
          .map((key) => ({
            option: t(`entities:customerType${key.toUpperCase()}`),
            value: key.toLowerCase(),
          })),
      ],
      title: "filterLabels:customerTypes",
      id: "type",
      defaultValue: OPTION_ALL,
    }),
    [t, config],
  );

  const tenantCategoryFilter: FilterSort = useMemo(
    () => ({
      type: "filter",
      menuType: FilterSortMenuType.Select,
      options: [
        {
          option: "filterLabels:allCustomerTypes",
          value: "all",
          translate: true,
        },
        ...(usedTenantCategories || []).map((category) => ({
          option: category.name,
          value: category.id,
        })),
      ],
      title: "filterLabels:tenantCategories",
      id: "category",
      defaultValue: OPTION_ALL,
    }),
    [usedTenantCategories],
  );

  const accessParentFilter: FilterSort = useMemo(
    () => ({
      type: "filter",
      menuType: FilterSortMenuType.Select,
      options: [
        {
          option: "filterLabels:allAccessParents",
          value: "all",
          translate: true,
        },
        ...(accessParents || []).map((ap) => ({
          option: ap.name,
          value: ap.id,
        })),
      ],
      title: "entities:accessParents",
      id: "accessParentId",
      defaultValue: OPTION_ALL,
    }),
    [accessParents],
  );

  const searchDebounced = useRef(
    throttle(500, (value?: string) => {
      setNameFilter(value);
    }),
  );

  const onSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      searchDebounced.current(value || undefined);
    },
    [searchDebounced],
  );

  const filters = useMemo(
    () => [
      isMWM ? customerTypeFilter : tenantCategoryFilter,
      accessParentFilter,
    ],
    [isMWM, customerTypeFilter, tenantCategoryFilter, accessParentFilter],
  );

  const onLinkClick = useCallback(async () => {
    setIsQRExportLoading(true);
    const token = await getTokenSilently();

    const headers: HeadersInit = {
      language,
      authorization: token as string,
      "Content-Type": "application/json",
    };

    setMultiTenantHeaders(selectedSite, headers);

    const exportUrl = getExportUrl("qr/pdf");
    const response = await fetch(exportUrl, {
      method: "POST",
      headers,
      body: JSON.stringify({
        terminalId: currentTerminal?.id,
        selectedIds,
      }),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const titleKey = terminalConfig?.includeWasteTypeInTenantQRs
      ? "exportLabels:tenantQRFileNameWithWasteStreams"
      : "exportLabels:tenantQRFileName";

    const fileName = `${t(titleKey)}_${moment().tz(timezone).format("YYYYMMDD")}`;
    addAndTriggerDownloadElement(url, `${fileName}.pdf`);

    setIsQRExportLoading(false);
  }, [
    getTokenSilently,
    currentTerminal?.id,
    selectedSite,
    language,
    selectedIds,
    t,
    timezone,
    setIsQRExportLoading,
    terminalConfig,
  ]);

  const linkLabel = useMemo(() => {
    let label = t("actions:printQRCodes");

    if (!selectedIds.length) {
      label += ` (${t("genericLabels:all").toLowerCase()})`;
    } else {
      label += ` (${selectedIds.length})`;
    }

    return label;
  }, [t, selectedIds]);

  const customersSection: MultiListSectionConfig<typeof tenants> = useMemo(
    () => ({
      type: "table",
      title: "",
      headers: tableHeaders,
      data: tenants,
      isLoading: isLoadingCustomers && !isFetchingNextPage,
      emptyViewType: nameFilter ? "noCustomerResults" : "noCustomers",
      onRowClick: (row) => {
        const searchParams = window.location.search;
        const rowId = row["id"] as string;
        if (selectedIds.includes(rowId)) {
          setSelectedIds(selectedIds.filter((id) => id !== rowId));
          navigate(`/customer/manage${searchParams}`);
        } else {
          setSelectedIds([rowId]);
          const newPath = `/customer/manage/${rowId}${searchParams}`;
          navigate(newPath);
        }
      },
      action: terminalConfig?.useTenantQRs
        ? {
            label: linkLabel,
            isLoading: isQRExportLoading,
            onClick: onLinkClick,
          }
        : undefined,
      isRowSelected: ({ row }) => selectedIds.includes(row["id"]),
      customButton:
        isRealEstate || !config?.useExternalCRM ? (
          <AddButton onClick={createCustomer} />
        ) : undefined,
      drawer: {
        content: (
          <CustomerDrawer
            selectedCustomers={selectedCustomers}
            areAllSelected={allSelected}
            filterState={tenantsFilterState}
            sortingState={sortingState}
            nameFilter={nameFilter}
          />
        ),
      },
      hasMultipleSelectFunctionality: true,
      labelSingle: "entities:customer",
      labelMultiple: "entities:customers",
      showExportButton: false,
      pagination: {
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
      },
      updateSorting: setSortingState,
      filters,
      onFilterOptionChange: setTenantsFilterState,
      useSearchParams: true,
      onAllSelected: setAllSelected,
      searchProps: {
        placeholder: t("formLabels:customerReferenceSearchPlaceholder"),
        onChange: onSearch,
      },
    }),
    [
      selectedIds,
      setSelectedIds,
      createCustomer,
      selectedCustomers,
      tenants,
      navigate,
      isLoadingCustomers,
      tableHeaders,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      setSortingState,
      setTenantsFilterState,
      isRealEstate,
      allSelected,
      config,
      setAllSelected,
      filters,
      sortingState,
      tenantsFilterState,
      t,
      nameFilter,
      onSearch,
      onLinkClick,
      terminalConfig?.useTenantQRs,
      linkLabel,
      isQRExportLoading,
    ],
  );

  const goToImport = useCallback(() => {
    if (accessParentsLength === 0) {
      showModal(<NoAccessParentsForCustomersModal />);
      return;
    } else {
      navigate("/customer/manage/import");
    }
  }, [navigate, accessParentsLength, showModal]);

  const initialState: useMultiListInitialState = {
    isDrawerOpen: !!selectedCustomers.length || !!customerIdFromUrl,
  };

  const showImportButton = useMemo(
    () =>
      (isRealEstate && isCarrotAdmin()) || (isMWM && !config?.useExternalCRM),
    [isRealEstate, config, isMWM],
  );

  return (
    <Page
      fullHeight
      title="sidebarLabels:manageCustomers"
      button={
        showImportButton
          ? {
              label: "importLabels:importCustomers",
              onClick: goToImport,
              type: "button",
              color: "primary",
            }
          : undefined
      }
    >
      <MultiListTable {...{ sections: [customersSection], initialState }} />
    </Page>
  );
};
